import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { priceOptions } from '../../../common/helpers/domain/priceOptions';
import { isDesktop } from '../../../common/helpers/utility/screenHelper';
import searchResource from '../../../locales/th/search.json';
import { WholeState as CommonState } from '../../../store/common/type';
import {
  PriceInputFilterWrapper,
  PriceInputFlexItem,
} from './PriceInputFilter.style';
import SelectInput from './SelectInput';

const PriceInputFilter: React.FC<Props> = ({
  minPrice,
  maxPrice,
  onMinPriceChange,
  onMaxPriceChange,
}) => {
  const defaultOption = {
    key: 'any',
    name: searchResource.searchLocation.any,
  };
  const deviceType = useSelector<CommonState, string>((state) =>
    state.common ? state.common.deviceType : 'desktop',
  );

  const isMobile = !isDesktop(deviceType);

  const minPriceOptions = priceOptions
    .filter(
      (option) =>
        option.price === null ||
        !Number.isInteger(maxPrice) ||
        option.price <= maxPrice,
    )
    .map(({ display, price }) => ({ key: price.toString(), name: display }));

  const maxPriceOptions = priceOptions
    .filter(
      (option) =>
        option.price === null ||
        !Number.isInteger(minPrice) ||
        option.price >= minPrice,
    )
    .map(({ display, price }) => ({ key: price.toString(), name: display }));

  const parsePriceSelect = useCallback((price: string): number | undefined => {
    const parsedPrice = parseInt(price, 10);
    return Number.isNaN(parsedPrice) ? undefined : parsedPrice;
  }, []);

  return (
    <PriceInputFilterWrapper data-test={'price-filter'}>
      <PriceInputFlexItem isMobile={isMobile}>
        <SelectInput
          options={[defaultOption, ...minPriceOptions]}
          labelText={searchResource.searchFilter.minPrice}
          qaHook="min-price"
          selectedOptionKey={
            minPrice?.toString() ?? (isMobile ? 'any' : undefined)
          }
          onSelect={(key) => onMinPriceChange(parsePriceSelect(key))}
          isMobile={isMobile}
        />
      </PriceInputFlexItem>
      <PriceInputFlexItem
        isMobile={isMobile}
        style={{ marginLeft: isMobile ? '8px' : '0' }}
      >
        <SelectInput
          options={[defaultOption, ...maxPriceOptions]}
          labelText={searchResource.searchFilter.maxPrice}
          qaHook="max-price"
          selectedOptionKey={
            maxPrice?.toString() ?? (isMobile ? 'any' : undefined)
          }
          onSelect={(key) => onMaxPriceChange(parsePriceSelect(key))}
          isMobile={isMobile}
        />
      </PriceInputFlexItem>
    </PriceInputFilterWrapper>
  );
};

interface Props {
  minPrice: number | null | undefined;
  maxPrice: number | null | undefined;
  onMinPriceChange: (minPrice: number) => void;
  onMaxPriceChange: (maxPrice: number) => void;
}

export default PriceInputFilter;
